"use client";

import { useState } from "react";
import { PressLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import Footer from "@/app/components/footer";
import Header from "@/app/components/header";
import { Box, Link, Typography } from "@mui/material";
import ScrollReveal from "@/app/components/ScrollReveal";

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

export type PressArticle = {
  source: string;
  image: string;
  /** Short line for hero emphasis or tile — derived from coverage */
  punchy_snippet: string;
  quote: string;
  readMoreUrl: string;
  attribution?: string;
};

/** Featured article first (hero); remaining render as tiles left to right. */
const articles: PressArticle[] = [
  {
    source: "People",
    image:
      "https://storage.googleapis.com/cuo-website/People%20Magazine%20Cover%20Photo.jpg",
    punchy_snippet:
      "N.Y.C. Comedy Duo Went Viral for Their Unconventional Shows. Now They Have a New Trick Up Their Sleeve (Exclusive)",
    quote:
      "Ethan Mansoor and David Levine have made a name for themselves with their viral pop-up comedy shows in unconventional locations and are now bringing their unique approach to magic.",
    readMoreUrl:
      "https://people.com/underground-overground-comedy-duo-announces-unexpected-new-venture-exclusive-12013357",
  },
  {
    source: "The New Yorker",
    image: "/images/press-6.webp",
    punchy_snippet:
      "Katz's Delicatessen...agreed to let a couple of twenty-four-year-olds stage a standup show among the pastrami and the Cel-Ray soda.",
    quote:
      "Katz's Deli, for the first time in its 135-year history, is transformed into a comedy venue by NYC natives Ethan Mansoor and David Levine, founders of ComedyUO.",
    readMoreUrl:
      "https://www.newyorker.com/magazine/2022/12/26/two-comedians-walk-into-a-deli",
  },
  {
    source: "Deadline",
    image: "https://storage.googleapis.com/cuo-website/Deadline+Photo.jpg",
    punchy_snippet:
      "EXCLUSIVE: Underground Overground Comedy signs with WME, from one pandemic show to something much bigger.",
    quote:
      "EXCLUSIVE: David Levine and Ethan Mansoor, co-founders of Underground Overground Comedy, have signed with WME. What began for the duo as a single show for family and friends during the pandemic quickly grew into something much bigger.",
    readMoreUrl:
      "https://deadline.com/2025/08/underground-overground-comedy-signs-wme-1236495564/",
  },
  {
    source: "New York Post",
    image: "/images/paragon-stage.png",
    punchy_snippet:
      "NYC's hottest comedy show, with a 20,000-person waitlist, is in an iconic sports store",
    quote:
      "The historic Paragon Sports store in Manhattan hosted Comedy UO for a two-day event featuring 30 comics performing across the store's eight floors.",
    readMoreUrl:
      "https://nypost.com/2024/05/07/lifestyle/nyc-comedy-show-with-11k-person-waitlist-is-in-a-sports-store/",
  },
  {
    source: "Time Out",
    image: "https://storage.googleapis.com/cuo-website/Empire+State+Building+Comedy+Show.jpg",
    punchy_snippet:
      "The first-ever comedy show is happening inside the Empire State Building",
    quote:
      "March 6, the Empire State Building will host its first-ever comedy show, transforming one of the world's most famous landmarks into an intimate stand-up venue for one night only. The event pairs the Art Deco icon with Underground Overground Comedy, the NYC collective known for staging sold-out shows in venues ranging from Katz's Deli to laundromats and vintage shops.",
    readMoreUrl:
      "https://www.timeout.com/newyork/news/the-first-ever-comedy-show-is-happening-inside-the-empire-state-building-030226",
    attribution: "Photo courtesy of Shutterstock",
  },
  {
    source: "NBC New York",
    image: "https://storage.googleapis.com/cuo-website/New%20York%20Live%20Promo.jpg",
    punchy_snippet: "Have you ever seen stand-up comedy at a steakhouse?",
    quote:
      "NBC New York's New York Live visits ComedyUO for stand-up in an unexpected steakhouse setting.",
    readMoreUrl:
      "https://www.nbcnewyork.com/video/entertainment/the-scene/new-york-live/have-you-ever-seen-stand-up-comedy-at-a-steakhouse/6490982/",
  },
  {
    source: "People",
    image: "/images/press-7.webp",
    punchy_snippet:
      "Meet Two Best Friends Who Are Bringing a Pop-Up Comedy Show to N.Y.C.'s Iconic Katz's Delicatessen",
    quote:
      "Underground Overground Comedy has smuggled laughter into laundromats, barbershops, and now, the hallowed halls of Katz's Deli. With 10,000 waitlisted fans, Comedy UO is proving comedy can bloom in the most unexpected locales.",
    readMoreUrl:
      "https://people.com/movies/meet-two-best-friends-who-are-bringing-a-pop-up-comedy-show-to-n-y-c-s-iconic-katzs-delicatessen/",
  },
  {
    source: "Untapped NYC",
    image: "/images/press-3.webp",
    punchy_snippet:
      "This Pop-Up Comedy Show Brings Laughs to Unexpected NYC Locations",
    quote:
      "With the help of Underground Overground Comedy, your local laundromat, barber, or deli can also moonlight as your local comedy venue. Expect free drinks and unique perks at ComedyUO's shows.",
    readMoreUrl: "https://www.untappedcities.com/nyc-underground-overground-comedy/",
  },
  {
    source: "Eater",
    image: "/images/press-4.webp",
    punchy_snippet:
      "A Popular Underground Comedy Show Returns to Katz's Deli",
    quote:
      "ComedyUO returns to the iconic Katz's Delicatessen to serve up another round of laughs, alongside a pastrami sandwich and side of crispy fries.",
    readMoreUrl:
      "https://ny.eater.com/2023/10/24/23928503/katz-deli-comedy-show-november-2023",
  },
];

function PressHero({ article }: { article: PressArticle }) {
  return (
    <Box
      sx={{
        position: "relative",
        /* Mobile: height follows copy so the page scrolls naturally; desktop: tall band */
        minHeight: { xs: "auto", md: "min(72vh, 900px)" },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        fontFamily: pageFont,
        boxSizing: "border-box",
      }}
    >
      <Box
        component="img"
        src={article.image}
        alt=""
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          filter: "blur(1px)",
          transform: "scale(1.04)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.62)",
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          px: { xs: 2, sm: 2.5, md: 4 },
          py: { xs: 5, sm: 6, md: 8 },
          pt: { xs: "max(24px, env(safe-area-inset-top))", md: 8 },
          pb: { xs: 5, md: 8 },
          maxWidth: { xs: "100%", sm: 960 },
          width: "100%",
          mx: "auto",
          textAlign: "center",
          boxSizing: "border-box",
        }}
      >
        <Typography
          sx={{
            fontFamily: pageFont,
            fontSize: { xs: "clamp(0.65rem, 2.5vw, 0.75rem)", sm: "0.75rem" },
            fontWeight: 600,
            letterSpacing: { xs: "0.14em", sm: "0.2em" },
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.92)",
            mb: 2,
            px: { xs: 0.5, sm: 0 },
          }}
        >
          {article.source}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: pageFont,
            fontWeight: 700,
            fontSize: {
              xs: "clamp(1.25rem, 5vw + 0.35rem, 1.85rem)",
              sm: "clamp(1.5rem, 3.5vw + 0.5rem, 2.35rem)",
              md: "clamp(2rem, 2.2vw + 1.25rem, 3rem)",
            },
            lineHeight: { xs: 1.2, md: 1.15 },
            letterSpacing: "-0.02em",
            color: "#fff",
            mb: 2,
            wordBreak: "break-word",
            overflowWrap: "break-word",
            hyphens: "auto",
            WebkitHyphens: "auto",
          }}
        >
          &ldquo;{article.punchy_snippet}&rdquo;
        </Typography>
        <Box
          sx={{
            width: 56,
            height: 1,
            backgroundColor: "rgba(255,255,255,0.35)",
            mx: "auto",
            mb: 2,
          }}
        />
        <Typography
          sx={{
            fontFamily: pageFont,
            fontSize: {
              xs: "clamp(0.875rem, 3.2vw, 1rem)",
              sm: "clamp(0.9375rem, 2vw, 1.0625rem)",
              md: "1.125rem",
            },
            fontWeight: 500,
            lineHeight: { xs: 1.62, md: 1.65 },
            color: "rgba(255,255,255,0.88)",
            maxWidth: 720,
            mx: "auto",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {article.quote}
        </Typography>
        {article.attribution && (
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: { xs: "0.75rem", md: "0.8rem" },
              fontStyle: "italic",
              color: "rgba(255,255,255,0.45)",
              mt: 2,
            }}
          >
            {article.attribution}
          </Typography>
        )}
        <Box sx={{ mt: { xs: 2.5, md: 3 } }}>
          <Link
            href={article.readMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              fontFamily: pageFont,
              fontWeight: 600,
              fontSize: { xs: "clamp(0.8rem, 2.5vw, 0.875rem)", md: "0.875rem" },
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#fff",
              borderBottom: "1px solid rgba(255,255,255,0.4)",
              pb: 0.5,
              display: "inline-flex",
              alignItems: "center",
              minHeight: 44,
              WebkitTapHighlightColor: "rgba(255,255,255,0.12)",
              "&:hover": { borderBottomColor: "#fff" },
            }}
          >
            Read coverage
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

function PressTile({ article }: { article: PressArticle }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      component="a"
      href={article.readMoreUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${article.source}: read full coverage`}
      sx={{
        display: "block",
        height: "100%",
        borderRadius: { xs: "10px", md: "12px" },
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.1)",
        position: "relative",
        aspectRatio: "2 / 3",
        maxWidth: "100%",
        cursor: "pointer",
        textDecoration: "none",
        color: "inherit",
        transition: "transform 0.35s ease, box-shadow 0.35s ease",
        WebkitTapHighlightColor: "rgba(255,255,255,0.08)",
        "@media (hover: hover) and (pointer: fine)": {
          "&:hover": {
            transform: "scale(1.03)",
            boxShadow: "0 24px 48px rgba(0,0,0,0.45)",
          },
        },
      }}
    >
      <Box
        component="img"
        src={article.image}
        alt=""
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 42%, transparent 72%)",
        }}
      />
      {/* Quote fills space above; outlet pinned to bottom so names line up across the row */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          top: "36%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          p: { xs: 2, sm: 2.5, md: 3 },
          pt: 1,
          pb: { xs: "3.25rem", sm: "3.5rem", md: "3.75rem" },
          fontFamily: pageFont,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: {
                xs: "clamp(0.8rem, 2.5vw, 0.9375rem)",
                md: "1rem",
              },
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.95)",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              ...(expanded
                ? { maxHeight: { xs: "40vh", md: "36vh" }, overflowY: "auto" }
                : {
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }),
            }}
          >
            &ldquo;{article.punchy_snippet}&rdquo;
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            sx={{
              display: "inline-block",
              margin: 0,
              marginTop: 0.5,
              padding: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              fontFamily: pageFont,
              fontSize: { xs: "0.8rem", md: "0.875rem" },
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "lowercase",
              color: "rgba(255,255,255,0.75)",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              "&:hover": { color: "#fff" },
            }}
          >
            {expanded ? "less" : "more"}
          </Box>
        </Box>
        <Typography
          sx={{
            position: "absolute",
            left: { xs: 16, sm: 20, md: 24 },
            right: { xs: 16, sm: 20, md: 24 },
            bottom: { xs: 16, sm: 20, md: 24 },
            fontWeight: 700,
            fontSize: {
              xs: "clamp(0.8rem, 2.8vw, 1rem)",
              md: "1.125rem",
            },
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#fff",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {article.source}
        </Typography>
      </Box>
    </Box>
  );
}

export default function Press() {
  const [featured, ...rest] = articles;

  return (
    <>
      <PressLayoutHelmet />
      <Header variant="dark" />
      <Box
        component="main"
        sx={{
          backgroundColor: "#121214",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "clip",
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <PressHero article={featured} />

        <ScrollReveal variant="fadeUp">
        <Box
          sx={{
            py: { xs: 4, sm: 5, md: 7 },
            pb: { xs: "max(32px, env(safe-area-inset-bottom, 0px))", md: 7 },
            width: "100%",
            maxWidth: "1536px",
            minWidth: 0,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            boxSizing: "border-box",
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: pageFont,
              fontWeight: 700,
              fontSize: { xs: "clamp(1.1rem, 4vw, 1.35rem)", md: "1.5rem" },
              color: "rgba(255,255,255,0.9)",
              mb: { xs: 1.5, md: 2.5 },
              letterSpacing: "0.02em",
            }}
          >
            More coverage
          </Typography>
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: { xs: "clamp(0.8rem, 3vw, 0.875rem)", md: "0.875rem" },
              color: "rgba(255,255,255,0.55)",
              mb: 2,
            }}
          >
            Scroll sideways to browse
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: { xs: 2, sm: 2.5 },
              overflowX: "auto",
              overflowY: "hidden",
              WebkitOverflowScrolling: "touch",
              scrollSnapType: { xs: "x mandatory", md: "x mandatory" },
              scrollPaddingLeft: { xs: 0, sm: 0 },
              scrollPaddingRight: { xs: "max(8px, env(safe-area-inset-right, 0px))", sm: 0 },
              overscrollBehaviorX: "contain",
              pb: 1.5,
              mx: { xs: -2, sm: -3, md: -4 },
              px: { xs: 2, sm: 3, md: 4 },
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: 4,
              },
            }}
          >
            {rest.map((article, i) => (
              <ScrollReveal key={article.source + article.readMoreUrl} variant="fadeUp" delay={i * 0.08}>
              <Box
                sx={{
                  flex: "0 0 auto",
                  width: {
                    xs: "min(calc(100vw - 48px), 280px)",
                    sm: "min(280px, 40vw)",
                    md: 300,
                  },
                  maxWidth: "min(100%, 300px)",
                  scrollSnapAlign: "start",
                }}
              >
                <PressTile article={article} />
              </Box>
              </ScrollReveal>
            ))}
          </Box>
        </Box>
        </ScrollReveal>
      </Box>
      <Footer />
    </>
  );
}
